import { Epic, ofType } from 'redux-observable';
import { of, concat, EMPTY } from 'rxjs';
import { mergeMap, catchError, map, tap } from 'rxjs/operators';

import { ToastType } from '../../models/general';
import { GENERAL } from '../../../constants';
import { IAction, IRootState, IEpicDependencies } from '../rootState';
import { actions, ActionType } from './actions';
import { coreState } from '../core';
import { generalState } from '../general';
import { handleError, handleToastError } from '../core/operators';
import { fileState } from '../file';
import { authState } from '../auth';
import { beautifyErrors } from 'utils';

export const userSignUp: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.SIGN_UP_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_SIGN_UP, true)),
        deps.apiService.createAccount(payload.token, payload.password).pipe(mergeMap(() => EMPTY)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_SIGN_UP, false))
      ).pipe(
        catchError(error => {
          let err = '';
          if (typeof error === 'object') {
            if (error.errors && Object.keys(error.errors).length > 0) {
              Object.keys(error.errors).forEach(e => {
                if (typeof error.errors[e] === 'string') {
                  err = (err.length > 0 ? ', ' : '') + error.errors[e];
                } else if (error.errors[e].length > 0) {
                  error.errors[e].forEach(f => {
                    if (f.indexOf('Amazon.CognitoIdentityProvider.Model.UsernameExistsException') >= 0) {
                      err = (err.length > 0 ? ', ' : '') + 'Account with these credentials already exists. Please contact customer support.';
                    } else {
                      err = (err.length > 0 ? ', ' : '') + f;
                    }
                  });
                }
              });
            } else if (error.message) {
              err = error.message;
            }
          }
          return of(coreState.actions.epicError(err), generalState.actions.setLoading(GENERAL.LOADING_KEY.AUTH_SIGN_UP, false, true, err));
        })
      )
    )
  );

export const validateToken: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.VALIDATE_TOKEN_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.VALIDATE_TOKEN, true)),
        deps.apiService.validateToken(payload.token).pipe(mergeMap(res => of(actions.validateTokenSuccess(res.email)))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.VALIDATE_TOKEN, false))
      ).pipe(
        catchError(error => {
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(
              GENERAL.LOADING_KEY.VALIDATE_TOKEN,
              false,
              true,
              error.response?.errors && (error.response?.errors?.EXPIRED_TOKEN || error.response?.errors?.USER_ALREADY_REGISTERED)
                ? {
                    errorType: error.response?.errors?.EXPIRED_TOKEN ? 'EXPIRED_TOKEN' : 'USER_ALREADY_REGISTERED',
                    message: error.response?.errors?.EXPIRED_TOKEN
                      ? error.response?.errors?.EXPIRED_TOKEN[0]
                      : error.response?.errors?.USER_ALREADY_REGISTERED[0],
                  }
                : error.title
            )
          );
        })
      )
    )
  );

export const fetchClientUserListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_CLIENT_USER_LIST_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_USER_LIST, true)),
        deps.apiService.getClientUserList(payload).pipe(map(res => actions.fetchClientUserListSuccess(payload.id, res.items, res.totalResults))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_USER_LIST, false))
      ).pipe(
        catchError(error =>
          of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_CLIENT_USER_LIST, false, true, error))
        )
      )
    )
  );

export const fetchProjectUserListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_PROJECT_USER_LIST_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_PROJECT_USER_LIST, true)),
        deps.apiService.getProjectUserList(payload).pipe(map(res => actions.fetchProjectUserListSuccess(payload.id, res.items, res.totalResults))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_PROJECT_USER_LIST, false))
      ).pipe(
        catchError(error =>
          of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_PROJECT_USER_LIST, false, true, error))
        )
      )
    )
  );

export const fetchUserProjectStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_USER_PROJECT_LIST_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_PROJECT_LIST, true)),
        deps.apiService.getUserList(payload.query).pipe(map(res => generalState.actions.setModalMap(res.items, res.totalResults))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_PROJECT_LIST, false))
      ).pipe(
        catchError(error =>
          of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_PROJECT_LIST, false, true, error))
        )
      )
    )
  );

export const fetchRoleListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_USER_ROLE_LIST_START),
    mergeMap(() =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_ROLE_LIST, true)),
        deps.apiService.getUserRoles().pipe(map(res => actions.fetchRoleListSuccess(res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_ROLE_LIST, false))
      ).pipe(
        catchError(error =>
          of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_ROLE_LIST, false, true, error))
        )
      )
    )
  );

export const fetchGroupSearchStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_GROUP_SEARCH_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_GROUP_SEARCH_LIST, true)),
        deps.apiService.getGroupList(payload.query, payload.isDataProtectionDisabled).pipe(map(res => actions.fetchGroupSearchSuccess(res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_GROUP_SEARCH_LIST, false))
      ).pipe(
        catchError(error =>
          of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_GROUP_SEARCH_LIST, false, true, error))
        )
      )
    )
  );

export const saveUserStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.SAVE_USER_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SAVE_USER, true)),
        deps.apiService
          .saveUser(payload.companyId, payload.user)
          .pipe(mergeMap(res => [actions.saveUserSuccess(res), generalState.actions.addToastStart(`User created successfully!`, ToastType.SUCCESS)])),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.SAVE_USER, false))
      ).pipe(
        catchError(error =>
          [
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.SAVE_USER, false, true, error),
            generalState.actions.addToastStart(beautifyErrors(error.errors) || error.title, ToastType.ERROR),
          ].filter(Boolean)
        )
      )
    )
  );

export const resetPassword: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.RESET_PASSWORD_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.RESET_PASSWORD, true)),
        deps.apiService.resetPassword(payload.email, payload.mobilePhoneNumber).pipe(mergeMap(() => EMPTY)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.RESET_PASSWORD, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.RESET_PASSWORD))
    )
  );

export const confirmResetPassword: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.CONFIRM_RESET_PASSWORD_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.CONFIRM_RESET_PASSWORD, true)),
        deps.apiService.confirmResetPassword(payload.token, payload.email, payload.password, payload.mobilePhoneNumber).pipe(mergeMap(() => EMPTY)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.CONFIRM_RESET_PASSWORD, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.CONFIRM_RESET_PASSWORD))
    )
  );

export const validateResetPasswordToken: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.VALIDATE_RESET_PASSWORD_TOKEN_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.VALIDATE_RESET_PASSWORD_TOKEN, true)),
        deps.apiService.validateResetPasswordToken(payload.token, payload.email, payload.mobilePhoneNumber).pipe(
          map(res => {
            return actions.validateResetPasswordTokenSuccess(res);
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.VALIDATE_RESET_PASSWORD_TOKEN, false))
      ).pipe(
        catchError(error => {
          const { email, mobilePhoneNumber } = (error?.request?.body && JSON.parse(error?.request?.body || '{}')) || {};
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.VALIDATE_RESET_PASSWORD_TOKEN, false, true, error),
            !email && mobilePhoneNumber ? generalState.actions.addToastStart('Incorrect Phone or Pin Code', ToastType.ERROR) : null
          );
        })
      )
    )
  );

export const changePassword: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.CHANGE_PASSWORD_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.CHANGE_PASSWORD, true)),
        deps.apiService.changePassword(payload.currentPassword, payload.newPassword).pipe(mergeMap(() => EMPTY)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.CHANGE_PASSWORD, false)),
        of(generalState.actions.addToastStart(`Password changed successfully!`, ToastType.SUCCESS))
      ).pipe(handleError(GENERAL.LOADING_KEY.CHANGE_PASSWORD))
    )
  );

export const updateProfileStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_PROFILE_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_PROFILE, true)),
        (state$.value.auth.isFcaUser ? deps.apiService.updateProfile(payload.data) : deps.apiService.updateCompanyUserProfile(payload.data)).pipe(
          map(() => actions.fetchProfileDataSuccess(payload.data))
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_PROFILE, false)),
        of(generalState.actions.addToastStart(`Profile updated successfully!`, ToastType.SUCCESS))
      ).pipe(
        handleToastError(GENERAL.LOADING_KEY.UPDATE_PROFILE, [
          'firstName',
          'lastName',
          'mobilePhoneNumber',
          'officePhoneExtension',
          'officePhoneNumber',
          'pictureUrl',
        ])
      )
    )
  );

export const fetchAccountStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_ACCOUNT_DATA_START),
    mergeMap(() =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_ACCOUNT, true)),
        (state$.value.auth.isFcaUser ? deps.apiService.getAccount() : deps.apiService.getCompanyUserAccount()).pipe(
          map(res => actions.fetchProfileDataSuccess(res))
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_ACCOUNT, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_ACCOUNT))
    )
  );

export const updateProfilePhotoStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_PROFILE_PHOTO_START),
    mergeMap(({ payload }) => {
      const file = Object.values(state$?.value?.file?.fileMap?.profilePhoto || {})[0];
      return [
        generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_PROFILE_PHOTO, true, false, undefined, GENERAL.TRACE_KEY.SAVE_UPLOAD_PROFILE_PHOTO),
        actions.uploadProfilePhotoStart(file),
        actions.updateProfilePhotoSuccess(),
        generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_PROFILE_PHOTO, false),
      ];
    })
  );

export const uploadProfilePhotoStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPLOAD_PROFILE_PHOTO_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPLOAD_PROFILE_PHOTO, true, false, undefined, GENERAL.TRACE_KEY.SAVE_UPLOAD_PROFILE_PHOTO)),
        deps.apiService
          .getProfilePhotoResource(payload.file.file.name)
          .pipe(map(response => fileState.actions.uploadFileStart(payload.file, response.url, response.fileId, GENERAL.TRACE_KEY.SAVE_UPLOAD_PROFILE_PHOTO))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPLOAD_PROFILE_PHOTO, false))
      ).pipe(handleToastError(GENERAL.LOADING_KEY.UPLOAD_PROFILE_PHOTO))
    )
  );

export const fetchUserProfileStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_USER_PROFILE_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_PROFILE, true)),
        deps.apiService
          .getCompanyUser(payload.companyId, payload.companyUserId)
          .pipe(map(res => actions.fetchUserProfileSuccess({ ...res, companyId: payload.companyId }))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_PROFILE, false))
      ).pipe(handleError(GENERAL.LOADING_KEY.FETCH_USER_PROFILE))
    )
  );

export const updateUserProfileStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.UPDATE_USER_PROFILE_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_USER, true)),
        deps.apiService
          .updateCompanyUser(payload.companyId, payload.companyUserId, payload.user)
          .pipe(
            mergeMap(res =>
              payload.isCurrentUserCompanyRemoved
                ? [
                    actions.updateUserProfileSuccess(res),
                    generalState.actions.addToastStart(
                      payload.user?.resendInvitation ? `User updated and invitation sent` : `User updated successfully!`,
                      ToastType.SUCCESS
                    ),
                    authState.actions.signOutStart(),
                  ]
                : [
                    actions.updateUserProfileSuccess(res),
                    generalState.actions.addToastStart(
                      payload.user?.resendInvitation ? `User updated and invitation sent` : `User updated successfully!`,
                      ToastType.SUCCESS
                    ),
                  ]
            )
          ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_USER, false))
      ).pipe(
        catchError(error =>
          [
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.UPDATE_USER, false, true, error),
            error.response && !error.response.errors && generalState.actions.addToastStart(error.title, ToastType.ERROR),
          ].filter(Boolean)
        )
      )
    )
  );

export const printPdf: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.PRINT_PDF),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.PRINT_WORKER_BADGE, true)),
        deps.apiService.printPDF(payload.badgeId).pipe(
          tap(res => deps.printerService.print(res.newFile)),
          mergeMap(() => EMPTY)
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.PRINT_WORKER_BADGE, false))
      ).pipe(
        catchError(error => of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.PRINT_WORKER_BADGE, false, true, error)))
      )
    )
  );

export const changeFcaUserStatus: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.CHANGE_FCA_USER_STATUS_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.CHANGE_FCA_USER_STATUS, true)),
        deps.apiService.changeFcaUserStatus(payload.companyId, payload.companyUserId, payload.enable).pipe(mergeMap(() => EMPTY)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.CHANGE_FCA_USER_STATUS, false)),
        of(generalState.actions.addToastStart(`User ${payload.enable ? 'enabled' : 'disabled'} successfully!`, ToastType.SUCCESS))
      ).pipe(
        catchError(error =>
          of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.CHANGE_FCA_USER_STATUS, false, true, error),
            generalState.actions.addToastStart(beautifyErrors(error.errors) || error.title, ToastType.ERROR)
          )
        )
      )
    )
  );

export const deleteUserStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.DELETE_USER_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.DELETE_USER, true)),
        deps.apiService.deleteUser(payload.companyId, payload.companyUserId).pipe(mergeMap(() => EMPTY)),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.DELETE_USER, false)),
        of(generalState.actions.addToastStart(`User deleted successfully!`, ToastType.SUCCESS))
      ).pipe(
        catchError(error =>
          of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.DELETE_USER, false, true, error),
            generalState.actions.addToastStart(beautifyErrors(error.errors) || error.title, ToastType.ERROR)
          )
        )
      )
    )
  );

export const fetchUserContactStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_USER_CONTACT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_CONTACT, true)),
        deps.apiService.fetchUserContact(payload.userId, payload.type).pipe(
          map(res => {
            return actions.fetchUserContactSuccess(res);
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_CONTACT, false))
      ).pipe(
        catchError(error => {
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_USER_CONTACT, false, true, error.response?.error),
            generalState.actions.addToastStart('Failed to load data', ToastType.ERROR)
          );
        })
      )
    )
  );

export const validatePassword: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.VALIDATE_PASSWORD_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.VALIDATE_PASSWORD, true)),
        deps.authService.signIn({ username: payload.userName, password: payload.password }).pipe(
          map(res => {
            return payload.validationType === 1
              ? actions.createUserContactStart(payload.userId, payload.type, payload.value)
              : payload.validationType === 2
              ? actions.makePrimaryUserContactStart(payload.userId, payload.contactId, payload.type, payload.value)
              : actions.removeUserContactStart(payload.userId, payload.contactId, payload.type, payload.value);
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.VALIDATE_PASSWORD, false))
      ).pipe(
        catchError(error => {
          error = error === 'Incorrect email or password' ? 'Incorrect password' : error;
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.VALIDATE_PASSWORD, false, true, error),
            generalState.actions.addToastStart(error, ToastType.ERROR)
          );
        })
      )
    )
  );

export const createUserContactStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.CREATE_USER_CONTACT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.CREATE_USER_CONTACT, true)),
        deps.apiService.createUserContact(payload.userId, payload.type, payload.value).pipe(
          mergeMap(res => {
            return of(
              actions.fetchUserContactStart(payload.userId, payload.type),
              generalState.actions.addToastStart('Contact created successfully.', ToastType.SUCCESS)
            );
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.CREATE_USER_CONTACT, false))
      ).pipe(
        catchError(error => {
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(
              GENERAL.LOADING_KEY.CREATE_USER_CONTACT,
              false,
              true,
              error.response?.errors?.contactValue ? error.response?.errors?.contactValue[0] : 'Failed to create contact'
            ),
            generalState.actions.addToastStart(
              error.response?.errors?.contactValue ? error.response?.errors?.contactValue[0] : 'Failed to create contact',
              ToastType.ERROR
            )
          );
        })
      )
    )
  );

export const verifyUserContactStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.VERIFY_USER_CONTACT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.VERIFY_USER_CONTACT, true)),
        deps.apiService.verifyUserContact(payload.userId, payload.contactId).pipe(
          mergeMap(res => {
            return of(
              actions.fetchUserContactStart(payload.userId, payload.type),
              generalState.actions.addToastStart(`Verification link sent to ${payload.value}.`, ToastType.SUCCESS)
            );
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.VERIFY_USER_CONTACT, false))
      ).pipe(
        catchError(error => {
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(
              GENERAL.LOADING_KEY.VERIFY_USER_CONTACT,
              false,
              true,
              error.response?.errors?.contactValue ? error.response?.errors?.contactValue[0] : 'Failed to verify contact'
            ),
            generalState.actions.addToastStart(
              error.response?.errors?.contactValue ? error.response?.errors?.contactValue[0] : 'Failed to verify contact',
              ToastType.ERROR
            )
          );
        })
      )
    )
  );

export const makePrimaryUserContactStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.MAKE_PRIMARY_USER_CONTACT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.MAKE_PRIMARY_USER_CONTACT, true)),
        deps.apiService.makePrimaryUserContact(payload.userId, payload.contactId).pipe(
          mergeMap(res => {
            return of(
              actions.fetchUserContactStart(payload.userId, payload.type),
              generalState.actions.addToastStart('Primary contact updated successfully.', ToastType.SUCCESS)
            );
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.MAKE_PRIMARY_USER_CONTACT, false))
      ).pipe(
        catchError(error => {
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(
              GENERAL.LOADING_KEY.MAKE_PRIMARY_USER_CONTACT,
              false,
              true,
              error.response?.errors?.contactValue ? error.response?.errors?.contactValue[0] : 'Failed to make contact primary'
            ),
            generalState.actions.addToastStart(
              error.response?.errors?.contactValue ? error.response?.errors?.contactValue[0] : 'Failed to make contact primary',
              ToastType.ERROR
            )
          );
        })
      )
    )
  );

export const removeUserContactStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.REMOVE_USER_CONTACT_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.REMOVE_USER_CONTACT, true)),
        deps.apiService.removeUserContact(payload.userId, payload.contactId).pipe(
          mergeMap(res => {
            return of(
              actions.fetchUserContactStart(payload.userId, payload.type),
              generalState.actions.addToastStart('Contact removed successfully.', ToastType.SUCCESS)
            );
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.REMOVE_USER_CONTACT, false))
      ).pipe(
        catchError(error => {
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(
              GENERAL.LOADING_KEY.REMOVE_USER_CONTACT,
              false,
              true,
              error.response?.errors?.contactValue ? error.response?.errors?.contactValue[0] : 'Failed to remove contact'
            ),
            generalState.actions.addToastStart(
              error.response?.errors?.contactValue ? error.response?.errors?.contactValue[0] : 'Failed to remove contact',
              ToastType.ERROR
            )
          );
        })
      )
    )
  );

export const validateUserStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.VALIDATE_USER_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.VALIDATE_USER, true)),
        deps.apiService.validateUser(payload.token).pipe(
          map(res => {
            return actions.validateUserSuccess(res);
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.VALIDATE_USER, false))
      ).pipe(
        catchError(error => {
          let errorMsg = '';
          if (error && error.response?.errors) {
            for (const err in error.response?.errors) {
              if (err) {
                errorMsg += (errorMsg.length > 0 ? ', ' : '') + error.response?.errors[err].join(',');
              }
            }
          } else {
            errorMsg = 'Failed to verify';
          }
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.VALIDATE_USER, false, true, errorMsg),
            // actions.validateUserSuccess(error),
            generalState.actions.addToastStart(errorMsg, ToastType.ERROR)
          );
        })
      )
    )
  );

export const approveUserStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.APPROVE_USER_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.APPROVE_USER, true)),
        deps.apiService.approveUser(payload.userId, payload.companyId, payload.projectId).pipe(
          map(res => {
            return actions.approveUserSuccess(true);
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.APPROVE_USER, false))
      ).pipe(
        catchError(error => {
          let errorMsg = '';
          if (error && error.response?.errors) {
            for (const err in error.response?.errors) {
              if (err) {
                errorMsg += (errorMsg.length > 0 ? ', ' : '') + error.response?.errors[err].join(',');
              }
            }
          }
          if (!errorMsg || errorMsg.length < 1) {
            errorMsg = error.response && error.response.detail ? error.response.detail : 'Failed to approve.';
          }
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.APPROVE_USER, false, true, errorMsg),
            // actions.validateUserSuccess(error),
            generalState.actions.addToastStart(errorMsg, ToastType.ERROR)
          );
        })
      )
    )
  );

export const denyUserStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.DENY_USER_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.DENY_USER, true)),
        deps.apiService.denyUser(payload.userId, payload.companyId, payload.projectId).pipe(
          map(res => {
            return actions.denyUserSuccess(true);
          })
        ),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.DENY_USER, false))
      ).pipe(
        catchError(error => {
          let errorMsg = '';
          if (error && error.response && error.response?.errors) {
            for (const err in error.response?.errors) {
              if (err) {
                errorMsg += (errorMsg.length > 0 ? ', ' : '') + error.response?.errors[err].join(',');
              }
            }
          }
          if (!errorMsg || errorMsg.length < 1) {
            errorMsg = error.response && error.response.detail ? error.response.detail : 'Failed to deny.';
          }
          return of(
            coreState.actions.epicError(error),
            generalState.actions.setLoading(GENERAL.LOADING_KEY.DENY_USER, false, true, errorMsg),
            // actions.validateUserSuccess(error),
            generalState.actions.addToastStart(errorMsg, ToastType.ERROR)
          );
        })
      )
    )
  );

export const fetchFCAAdminsListStart: Epic<IAction, IAction, IRootState, IEpicDependencies> = (action$, state$, deps) =>
  action$.pipe(
    ofType(ActionType.FETCH_FCA_ADMINS_START),
    mergeMap(({ payload }) =>
      concat(
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_FCA_ADMINS, true)),
        deps.apiService.getFCAAdmins(payload.query).pipe(map(res => actions.fetchFCAAdminsListSuccess(res))),
        of(generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_FCA_ADMINS, false))
      ).pipe(
        catchError(error => of(coreState.actions.epicError(error), generalState.actions.setLoading(GENERAL.LOADING_KEY.FETCH_FCA_ADMINS, false, true, error)))
      )
    )
  );

export const epics = [
  userSignUp,
  validateToken,
  resetPassword,
  fetchClientUserListStart,
  fetchProjectUserListStart,
  fetchGroupSearchStart,
  fetchUserProjectStart,
  fetchRoleListStart,
  saveUserStart,
  confirmResetPassword,
  changePassword,
  updateProfileStart,
  fetchAccountStart,
  updateProfilePhotoStart,
  uploadProfilePhotoStart,
  fetchUserProfileStart,
  updateUserProfileStart,
  validateResetPasswordToken,
  printPdf,
  changeFcaUserStatus,
  deleteUserStart,
  fetchUserContactStart,
  validatePassword,
  createUserContactStart,
  verifyUserContactStart,
  makePrimaryUserContactStart,
  removeUserContactStart,
  validateUserStart,
  approveUserStart,
  denyUserStart,
  fetchFCAAdminsListStart,
];
